import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Radio } from 'syngenta-digital-cropwise-react-ui-kit';
import './CropSeasonSections.css';

const UNASSIGNED_FIELDS_ID = '88888888-8888-8888-8888-888888888888';

const titleClassName =
  'px-12px py-6px text-xs leading-5 font-semibold text-neutral-60';
const getInputRowClassName = isSelected =>
  `w-full py-1 px-4 hover:bg-neutral-10 ${
    isSelected ? 'font-semibold bg-blue-10' : ''
  }`;

/**
 * Component for rendering season sections (Active, Upcoming, Past)
 */
const SeasonSection = ({
  title,
  seasons,
  allowMultipleSelection,
  isSeasonSelected,
  onSeasonChange,
  sectionType
}) => {
  if (seasons.length === 0) {
    return (
      <div
        className={`pt-1 pb-2 ${
          sectionType === 'past' ? '' : 'border-b border-neutral-20'
        }`}
      >
        <div className={titleClassName}>{title}</div>
        <div className="ml-3">No {title}</div>
      </div>
    );
  }

  return (
    <div
      className={`py-1 ${
        sectionType === 'past' ? '' : 'border-b border-neutral-20'
      }`}
    >
      <div className={titleClassName}>{title}</div>
      <div className="">
        {seasons.map(item => {
          const InputComponent =
            allowMultipleSelection || sectionType === 'active'
              ? Checkbox
              : Radio;

          return (
            <>
              <InputComponent
                key={item.id}
                className={getInputRowClassName(isSeasonSelected(item.id))}
                value={item.id}
                checked={isSeasonSelected(item.id)}
                onChange={e => onSeasonChange(item.id, e.target.checked)}
              >
                {item.name}
              </InputComponent>
              {/* Breaker div to avoid margins between consecutive checkboxes */}
              <div />
            </>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Component for rendering the unassigned fields option
 */
const UnassignedFieldsSection = ({
  allowMultipleSelection,
  isUnassignedSelected,
  onUnassignedChange
}) => {
  if (allowMultipleSelection) {
    return null; // Don't show unassigned fields in multiple selection mode
  }

  return (
    <div className="py-1 w-full border-t border-neutral-20">
      <div className={titleClassName}>Unassigned Fields</div>
      <ul className="pb-6px">
        <Radio
          className={getInputRowClassName(isUnassignedSelected)}
          value={UNASSIGNED_FIELDS_ID}
          checked={isUnassignedSelected}
          onChange={e => onUnassignedChange(e.target.checked)}
        >
          View Unassigned Fields
        </Radio>
      </ul>
    </div>
  );
};

/**
 * Main component for rendering all crop season sections
 */
const CropSeasonSections = ({
  items,
  allowMultipleSelection,
  isSeasonSelected,
  handleActiveSeasonChange,
  handleInactiveSeasonChange,
  handleUnassignedFieldsChange,
  selectedInactiveSeason = /** @type {string|null} */ (null)
}) => {
  const isUnassignedSelected = selectedInactiveSeason === UNASSIGNED_FIELDS_ID;

  return (
    <div
      style={{ width: 290, maxHeight: 209 }}
      className="crop-season-sections-scroll pt-2 overflow-y-auto overflow-x-hidden rounded border border-neutral-20"
    >
      <SeasonSection
        title="Active Seasons"
        seasons={items.active}
        allowMultipleSelection={allowMultipleSelection}
        isSeasonSelected={isSeasonSelected}
        onSeasonChange={handleActiveSeasonChange}
        sectionType="active"
      />
      <SeasonSection
        title="Upcoming Seasons"
        seasons={items.upcoming}
        allowMultipleSelection={allowMultipleSelection}
        isSeasonSelected={isSeasonSelected}
        onSeasonChange={handleInactiveSeasonChange}
        sectionType="upcoming"
      />
      <SeasonSection
        title="Past Seasons"
        seasons={items.past}
        allowMultipleSelection={allowMultipleSelection}
        isSeasonSelected={isSeasonSelected}
        onSeasonChange={handleInactiveSeasonChange}
        sectionType="past"
      />
      <UnassignedFieldsSection
        allowMultipleSelection={allowMultipleSelection}
        isUnassignedSelected={isUnassignedSelected}
        onUnassignedChange={handleUnassignedFieldsChange}
      />
    </div>
  );
};

SeasonSection.propTypes = {
  title: PropTypes.string.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.object).isRequired,
  allowMultipleSelection: PropTypes.bool.isRequired,
  isSeasonSelected: PropTypes.func.isRequired,
  onSeasonChange: PropTypes.func.isRequired,
  sectionType: PropTypes.oneOf(['active', 'upcoming', 'past']).isRequired
};

UnassignedFieldsSection.propTypes = {
  allowMultipleSelection: PropTypes.bool.isRequired,
  isUnassignedSelected: PropTypes.bool.isRequired,
  onUnassignedChange: PropTypes.func.isRequired
};

CropSeasonSections.propTypes = {
  items: PropTypes.shape({
    active: PropTypes.arrayOf(PropTypes.object).isRequired,
    upcoming: PropTypes.arrayOf(PropTypes.object).isRequired,
    past: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  allowMultipleSelection: PropTypes.bool.isRequired,
  isSeasonSelected: PropTypes.func.isRequired,
  handleActiveSeasonChange: PropTypes.func.isRequired,
  handleInactiveSeasonChange: PropTypes.func.isRequired,
  handleUnassignedFieldsChange: PropTypes.func.isRequired,
  selectedInactiveSeason: PropTypes.string
};

export default CropSeasonSections;
